import { decodedModelNeedsChoice, buildDecodedModelChoiceMessage } from '@/utils/carfax_make_model_resolution'

/*
 * The VIN decode's model-GROUP prompt, shared by both make/model selectors.
 *
 * The decode returns a model GROUP - "812", "911", "296", "Giulia" - where the catalogue holds the
 * specific rows behind it, so there is a question to put to the operator. Garrett, 17 Sep, having
 * found the group names across the live Ferrari and Porsche inventory: "Wow, it's every car."
 *
 * ⚠ SHARED because the same bug was already solved twice, differently. RoMakeModelSelector narrowed
 * the model dropdown in place (dd9abcbc51); MakeModelSelector raised this prompt (14ace9b07e). Both
 * files' comments cite the same Ferrari 296 anecdote. The two components are otherwise a 94% textual
 * copy of each other, so a third inline copy of this decision was the one outcome worth avoiding.
 *
 * WHAT THE HOST MUST PROVIDE (both selectors already do):
 *   data     selectedModel, selectedYear, selectedMakeModel, localModels
 *   computed makes
 *   methods  addToUniqueList(value, list), onSelectedModel(model)
 * Optionally `seedModelListAfterCancel()` - see onDecodedModelChoiceCancel.
 *
 * ⚠ THE TWO PATHS DIFFER IN WHETHER THE MODEL IS WRITTEN, and this mixin must serve both.
 *   Inventory WITHHOLDS the decoded group (VinNoSelector / mixins/inventory) because a free-text model
 *   saves with a null vehicleMakeVersionId and drops out of model reporting.
 *   RO does NOT withhold: RoServiceVehicle stores Make and Model as plain strings with no catalogue
 *   link at all (Domain/Entities/RoServiceVehicle.cs:10-11), so there is no id to lose, and
 *   RoServiceVehicleValidator requires Model NotEmpty - a withheld model is rejected by the server.
 *   On RO the prompt is therefore an upgrade offer over a value that is already there.
 */
export default {
  data: function () {
    return {
      // The prompt is on screen.
      showDecodedModelChoice: false,
      // Whether the prompt has a row ticked. It pre-checks nothing, so Apply starts disabled.
      decodedModelChoiceApplyable: false,
      /*
       * ⚠ The decode a selector ran ITSELF, which the record never sees. decodeVinForPopFills is
       * "offer, never apply" and deliberately writes nothing to selectedMakeModel - so on that path
       * there is no lookupResult on the record to derive the choice from, and reading only the record
       * would leave the operator with neither a chip nor a prompt.
       */
      decodedLookupResult: null,
      /*
       * ⚠ Load-bearing, not tidiness. The guard below deliberately lets a FREE-TEXT model through, so
       * that RO - where the decode always writes the group - still gets asked. That makes the choice
       * survive its own answer: after Cancel the model is free text, so without this flag
       * seedModelListAfterCancel would re-raise the prompt the operator just dismissed.
       */
      decodedModelChoiceAnswered: false
    }
  },
  computed: {
    /*
     * Whether the selector currently has a model question of its own on screen - the decode prompt,
     * or the select/configure modal the operator opened to answer it by hand.
     *
     * ⚠ The host needs this, and $refs are not reactive, so it is emitted rather than read. A host
     * that raises its own model prompt while this is true stacks a second answer to the same question
     * over the first: measured on New RO Service Vehicle with ZFF01SMA6R0311720, where the decode
     * offered 296 / 296 GTB / 296 GTS and the CARFAX prompt covered it offering 296 / 296 GTS.
     * Dean, 20 Sep: the CARFAX one must wait, and then only raise if the chosen model still differs.
     *
     * ⚠ isConfiguring belongs to the two selectors, not to this mixin. It is read rather than
     * required because a host that does not define it simply never opens a modal - undefined is not
     * true, so the computed is still correct. showTypedModelChoice is the same arrangement, owned by
     * the typedModelChoice mixin that both selectors also pull in.
     *
     * ⚠ ALL THREE QUESTIONS. The typed prompt was missing here, and the guard was right anyway only
     * by accident: the RO selector is mounted modal-only, and Buefy keeps a modal's body behind a
     * hard v-if, so the typed prompt could not appear unless isConfiguring was already true. That
     * made the guard depend on a third party's internals and on a prop default no host has
     * overridden yet. Naming the flag makes it correct by construction instead.
     */
    modelQuestionPending: function () {
      return this.showDecodedModelChoice === true ||
        this.showTypedModelChoice === true ||
        this.isConfiguring === true
    },
    /*
     * ⚠ Derived from the lookupResult, NOT from a flag the decode set. On inventory buildCustomMakeModel
     * decides whether to write a model and this decides whether to ask for one; if those two ever
     * disagreed the record would end with no model AND no prompt, which is worse than the bug being
     * fixed. They agree because both read decodedModelNeedsChoice over the same three values.
     *
     * ⚠ `id > 0`, NOT `!this.selectedModel`. A real catalogue row means the question is settled; free
     * text does not, and on RO free text is exactly the state the decode leaves behind. An earlier cut
     * tested `!this.selectedModel`, which was correct only while inventory was the sole caller.
     */
    decodedModelChoice: function () {
      if (this.decodedModelChoiceAnswered) return null
      if (this.selectedModel && this.selectedModel.id > 0) return null
      //⚠ The component's own decode first: it is the fresher of the two, and on that path it is the
      //ONLY one - decodeVinForPopFills never writes a lookupResult onto the record.
      const lookup = this.decodedLookupResult ||
        (this.selectedMakeModel ? this.selectedMakeModel.lookupResult : null)
      if (!lookup || !lookup.model) return null
      if (!decodedModelNeedsChoice(this.makes, lookup.make, lookup.model, lookup.productionYear)) return null

      const wanted = String(lookup.make).toUpperCase()
      return {
        make: this.makes.find((x) => x && x.name && String(x.name).toUpperCase() === wanted),
        model: lookup.model,
        productionYear: lookup.productionYear ? +lookup.productionYear : null
      }
    },
    /*
     * The decoded values shaped as a make/model, so the shared prompt can read them through
     * readMakeModelFields exactly as it reads a CARFAX rating. modelId 0 marks it as free text, which
     * is what colours the decoded row red beside the catalogue rows.
     */
    decodedModelChoiceMakeModel: function () {
      const choice = this.decodedModelChoice
      if (!choice) return null
      return { modelId: 0, customModelName: choice.model, productionYear: choice.productionYear }
    },
    decodedModelChoiceMessage: function () {
      const choice = this.decodedModelChoice
      return choice ? buildDecodedModelChoiceMessage(choice.model) : ''
    }
  },

  watch: {
    //⚠ Emitted on the change, not polled: the CARFAX result lands asynchronously, so the host can be
    //holding a deferred comparison that only this tells it to run.
    modelQuestionPending: function (newValue) {
      this.$emit('model-question-change', newValue === true)
    }
  },
  methods: {
    /*
     * A NEW decode arrived, so the previous vehicle's answer must not suppress this one - the operator
     * may have corrected a mistyped VIN.
     *
     * ⚠ SEPARATE from raiseDecodedModelChoice, and that separation is load-bearing. Cancel marks the
     * choice answered and then asks the host to reopen the model list; the host does that through
     * offerModelListWhenNotInCatalogue, which also raises. If raising cleared `answered`, the prompt
     * would reopen the instant it was dismissed. Only a genuine new decode clears it.
     *
     * @param lookupResult the selector's OWN decode, or null when the decode reached the record
     */
    beginDecodedModelChoice: function (lookupResult) {
      this.decodedLookupResult = lookupResult || null
      this.decodedModelChoiceAnswered = false
    },
    // Raises the prompt if there is a question to ask. Returns whether it did.
    raiseDecodedModelChoice: function () {
      if (!this.decodedModelChoice) return false
      this.decodedModelChoiceApplyable = false
      this.showDecodedModelChoice = true
      return true
    },
    /*
     * Answering it. The chosen row is either a catalogue model or the decoded group name kept as free
     * text, and both land through onSelectedModel - the one definition of what choosing a model means,
     * which also fires the change event the host saves on.
     */
    onDecodedModelChoiceApply: function (chosenRow) {
      //⚠ Nothing chosen must not close the prompt. Closing it would leave the record on whatever the
      //decode left and no way back to the question.
      if (!chosenRow) return

      const chosen = chosenRow.model && chosenRow.model.id > 0
        ? chosenRow.model
        : { id: -1, name: chosenRow.name, productionYear: this.selectedYear, trim: '', style: '' }

      this.addToUniqueList(chosen, this.localModels)
      this.selectedModel = chosen
      this.onSelectedModel(chosen)
      this.decodedModelChoiceAnswered = true
      this.showDecodedModelChoice = false
    },
    /*
     * Cancel means "I do not want to pick from your list", not "leave the model blank".
     *
     * ⚠ It FALLS BACK to the behaviour that preceded this prompt: the decoded group goes in as free
     * text, which raises the not-in-catalogue warning, and the model list opens filtered to it. An
     * earlier cut simply closed the prompt and relied on the required validator to stop the save.
     * Measured in the browser on ZFF83CLA4K0248000 that left an empty model field with no warning, no
     * chip and no filtered list - strictly less to act on than before the change existed. Dean, 20 Sep:
     * default to the text and show the previous behaviour.
     *
     * ⚠ Read the choice BEFORE marking it answered, because decodedModelChoice goes null the moment
     * the flag is set.
     */
    onDecodedModelChoiceCancel: function () {
      const choice = this.decodedModelChoice
      this.showDecodedModelChoice = false
      this.decodedModelChoiceAnswered = true
      if (!choice) return

      const freeText = {
        id: -1,
        name: choice.model,
        productionYear: this.selectedYear || choice.productionYear,
        trim: '',
        style: ''
      }
      this.addToUniqueList(freeText, this.localModels)
      this.selectedModel = freeText
      //⚠ Through onSelectedModel, not a bare assignment: nothing watches selectedModel, and onSave
      //reads selectedMakeModel - the defect that made the year and model pop-fill chips save nothing.
      this.onSelectedModel(freeText)
      /*
       * ⚠ TELL THE TYPED PROMPT THIS WAS ANSWERED, or the two prompts ping-pong. The call below
       * re-opens the model list and SEEDS THE SEARCH BOX with the group name; when the operator closes
       * that list, the typed path's close trigger finds leftover text and raises its own prompt about
       * the value just dismissed here. One shared latch, keyed by the value, is what stops it.
       *
       * ⚠ Guarded rather than assumed: a host that mixes in decodedModelChoice without
       * typedModelChoice is still valid, and this must not throw there.
       */
      if (typeof this.markTypedModelAnswered === 'function') {
        this.markTypedModelAnswered(choice.model)
      }
      this.offerModelListWhenNotInCatalogue()
    },
    onDecodedModelChoiceChanged: function (hasChoice) {
      this.decodedModelChoiceApplyable = hasChoice === true
    },
    /*
     * Raise the prompt, or - once it is answered - open the model list on the value the operator is
     * left looking at, filtered to it.
     *
     * ⚠ Both halves matter. A free-text model like "812" leaves the screen STATING the problem (a red
     * value and a warning) while offering no route out of it; the operator has to guess the answer is
     * in a 300-row dropdown. Opening it filtered is that route.
     *
     * ⚠ Shared rather than per-selector. This lived only in MakeModelSelector, which is why the RO
     * screen showed the warning and did nothing about it. The timing below was paid for in browser
     * debugging and is exactly the kind of thing that should not be re-derived in the fork.
     *
     * ⚠ activate(), not focus(). focusElement calls .focus() on the root element, which focuses the
     * control without opening the list - and the list is the whole point. activate() is
     * vue-multiselect's own opener (2.1.3); the fallback keeps this harmless if the ref is a stub or
     * the library changes.
     */
    offerModelListWhenNotInCatalogue: function () {
      const that = this
      /*
       * ⚠ TWO ticks, and the checks go INSIDE them, not before.
       *
       * The host assigns selectedMakeModel and emits preview-make-model in the same breath, so when
       * this runs the value watcher has not applied the decode yet - unresolvedModelName is still ''
       * and a check up here returns early on every decode. The modal body has not rendered either, so
       * $refs.model does not exist. The first tick lets the watcher land, the second lets the modal
       * render. Verified in the browser: one tick opens nothing.
       */
      this.$nextTick(() => {
        that.$nextTick(() => {
          /*
           * ⚠ Raising REPLACES the seeding rather than adding to it. Opening the dropdown behind a
           * prompt puts two answers to the same question on screen at once.
           */
          if (that.raiseDecodedModelChoice()) return
          const unresolved = that.unresolvedModelName
          if (!unresolved) return
          const model = that.$refs && that.$refs.model
          if (!model) return
          if (typeof model.activate === 'function') {
            model.activate()
          } else if (model.$el && typeof model.$el.focus === 'function') {
            model.$el.focus()
          }
          /*
           * ⚠ The value is seeded into the SEARCH box, not left as the selection to scroll past.
           * vue-multiselect's filteredOptions reads `search`, so typing it for the operator narrows a
           * 300-row Ferrari catalogue to the handful that actually matter - "812" surfaces
           * "812 Superfast", "812 GTS", "812 Competizione". Without it the list opens on the whole
           * catalogue alphabetically and the models being pointed at are nowhere near the top.
           */
          if (typeof model.updateSearch === 'function') {
            model.updateSearch(unresolved)
          }
        })
      })
    }
  }
}
